import {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useMemo,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react';
import { debounce } from 'lodash';
import { useInView } from 'react-intersection-observer';
import { useNetworkObserver } from 'data-layer-cr/dist/network';

// Network
import { INSPECCION_TRANSFER } from '../../store/quotation/requestIdentifiers';

export type UseInfiniteScrollParams<T> = {
  step?: number;
  requestIdentifier: string;
  referenceTotal: number;
  referenceList: T[];
  additionalFilters: string;
  getReferenceList: (params: {
    searchString?: string;
    additionalFilters?: string;
    skip?: number;
    step?: number;
  }) => void;
};

export type UseInfiniteScrollResult<T> = {
  ref: (node?: Element | null | undefined) => void;
  list: T[];
  setSearchQuery: Dispatch<SetStateAction<string>>;
  isLoading: boolean;
};

function useInfiniteScroll<E>({
  step = 12,
  additionalFilters,
  requestIdentifier,
  getReferenceList,
  referenceList,
  referenceTotal,
}: UseInfiniteScrollParams<E>): UseInfiniteScrollResult<E> {
  const [cursor, setCursor] = useState(0);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [list, setList] = useState<E[]>([]);
  const requestStatus = useNetworkObserver(requestIdentifier);
  const inspectionTransferStatus = useNetworkObserver(INSPECCION_TRANSFER);
  const { ref, inView } = useInView({ threshold: 0.9 });
  const firstUpdate = useRef(true);

  const refresh = useCallback(() => {
    setCursor(0);
    setTotal(0);
    setList([]);
    getReferenceList({ step });
  }, [getReferenceList, step]);

  // When step change fetch again because displayMode change
  useEffect(() => {
    if (!firstUpdate.current) {
      refresh();
    }
  }, [refresh]);

  // Refresh list quotations when status transfer inspeccion is succes
  useEffect(() => {
    if (inspectionTransferStatus === 'success') {
      setCursor(0);
      setTotal(0);
      setList([]);
      getReferenceList({
        step,
        searchString: searchQuery,
        additionalFilters,
        skip: 0,
      });
    }
  }, [
    inspectionTransferStatus,
    additionalFilters,
    getReferenceList,
    searchQuery,
    step,
  ]);

  useEffect(() => {
    /**
     * At first render the anchor is visible, but this is the initial state
     * so a fetch behavior is unwanted. Use this flag to avoid fetching in
     * first render.
     */
    if (firstUpdate.current) {
      firstUpdate.current = false;
    }
    // bootstrap
    getReferenceList({ step });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldFetchMore = useMemo(
    () =>
      cursor < total && requestStatus !== 'requesting' && !firstUpdate.current,
    [cursor, total, requestStatus],
  );

  /** Whenever search query changes, reset with debounce */
  const debouncedCallRef = useRef(
    debounce<
      (
        searchString: string,
        additionalFilters: string,
        condition: boolean,
      ) => void
    >((searchString, additionalFiltersString, condition) => {
      if (condition) {
        getReferenceList({
          searchString,
          additionalFilters: additionalFiltersString,
          skip: 0,
          step,
        });
        setCursor(0);
        setTotal(0);
        setList([]);
      }
    }, 300),
  ).current;
  /**
   * Note the call to useLayoutEffect is being used here instead of useEffect.
   * This is because our debouncedCall will reset the states causing a flickering screen
   * useLayoutEffect ensures the 'effects' take place before painting to the screen
   * read more here: https://dev.to/nibble/what-is-uselayouteffect-hook-and-when-do-you-use-it-3lan
   */
  useLayoutEffect(() => {
    debouncedCallRef(searchQuery, additionalFilters, !firstUpdate.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, additionalFilters]);

  /** Whenever observed anchor is being intersected, load more */
  useEffect(() => {
    if (inView && shouldFetchMore) {
      getReferenceList({
        searchString: searchQuery,
        additionalFilters,
        skip: cursor,
        step,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  /** Whenever reference list updates, append it to the local list */
  useLayoutEffect(() => {
    if (!firstUpdate.current) {
      setList([...list, ...referenceList]);
      setCursor(cursor + referenceList.length);
      setTotal(referenceTotal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceList]);

  useEffect(() => {
    setTotal(referenceTotal);
  }, [referenceTotal]);

  return {
    ref,
    list,
    setSearchQuery,
    isLoading: requestStatus === 'requesting',
  };
}

export default useInfiniteScroll;
